/* eslint-disable max-lines */
import { Cell, Column, Row, TableData } from '@hakimo-ui/hakimo/ui-table';
import { Label, SegmentedToggleOption } from '@hakimo-ui/shared/ui-base';
import { AdminTenant } from '@hakimo-ui/hakimo/types';
import { TenantsFilters } from '../types';

export const PAGE_SIZE = 20;

export const PRODUCT_TYPE_OPTIONS: SegmentedToggleOption[] = [
  { id: 'Motion', label: 'Remote Guarding' },
  { id: 'Vision', label: 'Scan' },
];

export const LOADING_STEPS = [
  'Creating tenant entities in database...',
  'User creation in progress...',
  'Granting access to hakmotion users for this tenant',
];

export const LOADING_STEP_DURATION = 1000;

export const TENANT_ID_MAX_LENGTH = 16;

export const validateTenantId = (
  value: string
): { isValid: boolean; error: string } => {
  const regex = /^[a-z0-9-]*$/;
  if (!value) {
    return { isValid: false, error: 'Tenant ID is required' };
  }
  if (!regex.test(value)) {
    return {
      isValid: false,
      error:
        'Use only lowercase letter (a-z), hyphens (-), and numbers (0-9). No spaces, capitals or special characters. Keep it concise.',
    };
  }
  if (value.length > TENANT_ID_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Tenant ID must be ${TENANT_ID_MAX_LENGTH} characters or fewer`,
    };
  }
  return { isValid: true, error: '' };
};

export const validateTenantName = (
  value: string
): { isValid: boolean; error: string } => {
  const regex = /^[a-zA-Z0-9]*$/;
  if (!value) {
    return { isValid: false, error: 'Tenant Name is required' };
  }
  if (!regex.test(value)) {
    return {
      isValid: false,
      error:
        'Use only Alphanumeric characters (A-Z a-z 0-9). No spaces, or special characters. Keep it concise.',
    };
  }
  return { isValid: true, error: '' };
};

export const columns: Column[] = [
  { id: 'id', name: 'Tenant ID' },
  { id: 'name', name: 'Tenant Name' },
  { id: 'locationCount', name: 'Locations' },
  { id: 'cameraCount', name: 'Cameras' },
  { id: 'audioDeviceCount', name: 'Audio Devices' },
  { id: 'productType', name: 'Product Type' },
];

export function getTableData(
  tenants: AdminTenant[],
  onClickRow: (
    tenantId: string,
    event: React.MouseEvent | React.KeyboardEvent
  ) => void
): TableData {
  return {
    columns,
    rows: tenants.map((tenant) => {
      const displayProductType =
        tenant.productType === 'Motion'
          ? 'Remote Guarding'
          : tenant.productType;

      const tenantIdCell = !tenant.isActive ? (
        <div className="flex items-center gap-2">
          <span>{tenant.id}</span>
          <Label text="Inactive" small />
        </div>
      ) : (
        tenant.id
      );

      const cells: Cell[] = [
        { value: tenantIdCell },
        { value: tenant.name },
        { value: tenant.locationCount },
        { value: tenant.cameraCount },
        { value: tenant.audioDeviceCount },
        { value: displayProductType },
      ];

      const rowData: Row = {
        id: tenant.id,
        cells,
        onClick: (event) => onClickRow(tenant.id, event),
      };

      return rowData;
    }),
  };
}

export function getSearchParams(
  search: string,
  filters: TenantsFilters,
  page?: number,
  pageSize?: number
): string {
  const params = new URLSearchParams();

  if (page) params.append('page', String(page));
  if (pageSize) params.append('pageSize', String(pageSize));
  if (search) params.append('tenantName', search);
  if (filters.locationCity) params.append('locationCity', filters.locationCity);
  if (filters.locationState)
    params.append('locationState', filters.locationState);
  if (filters.locationCountry)
    params.append('locationCountry', filters.locationCountry);
  if (filters.showInactiveTenants) params.append('showInactiveTenants', 'true');
  if (filters.productType) params.append('productType', filters.productType);

  return params.toString();
}

export function getUrlSearchParams(
  search: string,
  filters: TenantsFilters,
  page: number,
  viewMode: 'grid' | 'list'
): URLSearchParams {
  const params = new URLSearchParams();

  if (search) params.set('tenantName', search);
  if (filters.locationCity) params.set('locationCity', filters.locationCity);
  if (filters.locationState) params.set('locationState', filters.locationState);
  if (filters.locationCountry)
    params.set('locationCountry', filters.locationCountry);
  if (filters.showInactiveTenants) params.set('showInactiveTenants', 'true');
  if (filters.productType) params.set('productType', filters.productType);
  if (page > 1) params.set('page', String(page));
  if (viewMode !== 'grid') params.set('viewMode', viewMode);

  return params;
}
